// src/Views/AiAssistant/AiAssistant.js
// UPDATED WITH BASKETBALL V4 + HRHR V5.5 INTEGRATION
// 
// 🔧 TIMEZONE FIX: All date keys now use UTC to match backend
// This fixes the bug where users in different timezones saw incorrect usage counts
//
// ENGINE LIMITS:
// - V7.5 FREE: 3 searches/day, 2 matches/search
// - V7.5 TRIAL/PREMIUM: 3-50 searches/day, 10 matches/search
// - V10 TRIAL: 1 search/day, 3 matches/search
// - V10 PREMIUM: 4 searches/day, 3 matches/search
// - BASKETBALL V4 (Premium Only): 3 searches/day, 2 matches/search
// - HRHR V5.5 (Premium Only): 3 searches/day, 3 matches/search
//
// 🏀 Basketball V4 - Premium Exclusive Feature
// 🎯 HRHR V5.5 - Premium Exclusive Value Picks (Deep Defense + Win Logic)
// 🔒 HRHR Output Sanitization - Protects proprietary V5.5 methodology
// 🌟 Added App Store Rating Integration

import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './AiAssistant.css';
import ReactMarkdown from 'react-markdown';
import aiAssistantHero from '../../assets/images/ai-assistant-hero.png';
import Bookmaker from '../Bookmakers/Bookmakers';
import { Icon } from '@iconify/react';
import useAppRating from '../../hooks/useAppRating';

import {
    collection,
    addDoc,
    query,
    orderBy,
    limit,
    getDocs,
    deleteDoc,
    doc,
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';

import { useSubscription } from '../../hooks/useSubscription';
import Paywall from '../../components/Paywall.js';

// =======================================================
// CONFIGURATION
// =======================================================
const LOADING_STAGES = [
    { text: "Searching forms and statistics...", duration: 8000 },
    { text: "Generating predictions...", duration: null },
];

// 🏀 Basketball-specific loading stages
const BASKETBALL_LOADING_STAGES = [
    { text: "Analyzing H2H records & patterns...", duration: 6000 },
    { text: "Checking fatigue & schedules...", duration: 6000 },
    { text: "Generating predictions...", duration: null },
];

// 🎯 HRHR-specific loading stages
const HRHR_LOADING_STAGES = [
    { text: "Calculating implied probabilities...", duration: 5000 },
    { text: "Scanning for mispriced markets...", duration: 6000 },
    { text: "Identifying value edges...", duration: 5000 },
    { text: "Generating HRHR picks...", duration: null },
];

const MAX_HISTORY_ITEMS = 5;

// 🔥 WHOP TRIAL URL (Web only)
const WHOP_TRIAL_URL = "https://whop.com/predictai-labs/monthly-premium-plan/";

// 🔥 UPDATED LIMITS PER ENGINE (Including Basketball + HRHR)
const ENGINE_LIMITS = {
    v7_5: {
        FREE: { searchesPerDay: 3, searchesPerWeek: null, matches: 2 },
        TRIAL: { searchesPerDay: 3, searchesPerWeek: null, matches: 10 },
        PREMIUM: { searchesPerDay: 50, searchesPerWeek: null, matches: 10 }
    },
    v10: {
        FREE: { searchesPerDay: 0, searchesPerWeek: 0, matches: 0 },
        TRIAL: { searchesPerDay: 1, searchesPerWeek: null, matches: 3 },
        PREMIUM: { searchesPerDay: 4, searchesPerWeek: null, matches: 3 }  // 🔄 UPDATED: 4/day, 3 matches/search
    },
    // 🏀 Basketball V4 (Premium Only)
    basketball: {
        FREE: { searchesPerDay: 0, searchesPerWeek: null, matches: 0 },
        TRIAL: { searchesPerDay: 0, searchesPerWeek: null, matches: 0 },
        PREMIUM: { searchesPerDay: 3, searchesPerWeek: null, matches: 2 }
    },
    // 🎯 HRHR V5.5 (Premium Only) - 🔄 UPDATED: Was 2/day, 2 matches
    hrhr: {
        FREE: { searchesPerDay: 0, searchesPerWeek: null, matches: 0 },
        TRIAL: { searchesPerDay: 0, searchesPerWeek: null, matches: 0 },
        PREMIUM: { searchesPerDay: 3, searchesPerWeek: null, matches: 3 }
    }
};

// 🔥 iOS Detection Helper
const getIsIOS = () => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// 🔥 Get start of current week (Monday) - UTC
const getStartOfWeek = () => {
    const now = new Date();
    const day = now.getUTCDay();
    const diff = now.getUTCDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), diff));
    return monday;
};

// 🔥 UTC DATE HELPERS (Matches Backend)
// This fixes timezone mismatch where users in different timezones
// would see incorrect usage counts
const getUtcDayKey = () => {
    return new Date().toISOString().slice(0, 10); // "2025-01-16"
};

const getUtcWeekKey = () => {
    const monday = getStartOfWeek();
    return monday.toISOString().slice(0, 10); // Monday's date in UTC
};

// =======================================================
// 🔒 HRHR OUTPUT SANITIZATION - PROTECTS PROPRIETARY V5.5 LOGIC
// =======================================================
// This function strips internal methodology from HRHR responses
// to prevent users from reverse-engineering your system prompt.
// 
// REMOVES: BLACKLIST/CRISIS CHECK, RULES TRIGGERED, FILTERS APPLIED,
// REJECTED OPTIONS, and other internal methodology markers
// =======================================================
const sanitizeHRHROutput = (text) => {
    if (!text) return text;
    
    // Patterns to remove (case-insensitive, multiline)
    // Updated for HRHR V5.5 XML-based output format
    const patternsToRemove = [
        // Remove BLACKLIST/CRISIS CHECK sections (with emoji variations)
        /(?:🚫|⛔|🔴)?\s*\*?\*?BLACKLIST\/CRISIS CHECK:?\*?\*?[^\n]*(?:\n(?!(?:🎯|MATCH|📊|---|\n\n))[^\n]*)*/gi,
        
        // Remove RULES TRIGGERED sections
        /(?:✅|☑️|✔️)?\s*\*?\*?RULES TRIGGERED:?\*?\*?[^\n]*(?:\n(?!(?:🎯|MATCH|📊|⚠️|🚫|---|\n\n))[^\n]*)*/gi,
        
        // Remove FILTERS APPLIED sections
        /(?:⚠️|⚡|🔶)?\s*\*?\*?FILTERS APPLIED:?\*?\*?[^\n]*(?:\n(?!(?:🎯|MATCH|📊|🚫|---|\n\n))[^\n]*)*/gi,
        
        // Remove REJECTED OPTIONS sections (V5.5 format: 🚫 REJECTED:)
        /(?:🚫|❌|⛔)?\s*\*?\*?REJECTED(?:\s+OPTIONS)?:?\*?\*?[^\n]*(?:\n(?!(?:🎯|MATCH|📊|📝|---|\n\n))[^\n]*)*/gi,
        
        // Remove V5.5 FILTER CHECK tables
        /🔍\s*V5\.5\s*FILTER\s*CHECK:?[^\n]*(?:\n\|[^\n]*)*\n?/gi,
        
        // Remove standalone filter mentions
        /•\s*(?:Filter\s*\d+|Deep Defense|Quality Premium|BTTS Valid|Pattern Break|Scoring Drought)[^\n]*\n?/gi,
        
        // Remove "Away Suicide:" checks
        /•?\s*\*?\*?Away Suicide:?\*?\*?[^\n]*\n?/gi,
        
        // Remove edge calculation methodology
        /•?\s*\*?\*?Edge Calculation:?\*?\*?[^\n]*\n?/gi,
        
        // Remove confidence boost explanations
        /\(\+\d+%\s*(?:confidence\s*boost|edge\s*boost)[^)]*\)/gi,
        
        // Remove blacklist status lines
        /•?\s*\*?\*?(?:Blacklist|Crisis)\s*(?:Status|Check):?\*?\*?[^\n]*\n?/gi,
        
        // Remove "under V5.5 rules" or "under V3.0 rules" mentions
        /under\s+V(?:3\.0|5\.5)\s+rules/gi,
        
        // Remove internal tier references  
        /•?\s*Tier\s*\d+\s*-\s*(?:High|Medium|Low)\s*Confidence/gi,
        
        // Clean up HRHR methodology references (any version)
        /HRHR\s*V(?:3\.0|4\.0|5\.5)\s*(?:methodology|rules|system|logic)/gi,
        
        // Remove V5.5 Validation lines
        /V5\.5\s*Validation:?\s*[^\n]*\n?/gi,
        
        // Remove pre-selection checklist items
        /□\s*(?:DEEP DEFENSE|QUALITY PREMIUM|BTTS|PATTERN BREAK|CRISIS CAP|BLACKLIST)[^\n]*\n?/gi,
    ];
    
    let sanitized = text;
    
    // Apply all removal patterns
    patternsToRemove.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
    });
    
    // Clean up excessive newlines (more than 2 in a row)
    sanitized = sanitized.replace(/\n{3,}/g, '\n\n');
    
    // Clean up lines that are just bullet points or dashes
    sanitized = sanitized.replace(/^\s*[•\-]\s*$/gm, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    return sanitized;
};

const AiAssistant = () => {
    const {
        user,
        isPremium,
        isPaidPremium,
        isTrialActive,
        trialDaysLeft,
        hasUsedTrial,
        profile
    } = useAuth();

    const navigate = useNavigate();
    const answerRef = useRef(null);

    const { isPremium: isIapPremium } = useSubscription();
    
    // 🌟 App Rating Hook
    const { onPredictionSuccess } = useAppRating();
    
    // 🔥 Check if on iOS
    const isIOS = getIsIOS();

    // 🏀 NEW: Sport type state (football or basketball)
    const [sportType, setSportType] = useState('football');

    // Default: PAID Premium → V10 only, Trial/Free → V7.5
    const getDefaultAgent = () => {
        if (isIapPremium || isPaidPremium) return 'v10';
        return 'v7_5';
    };
    
    const [agent, setAgent] = useState('v7_5');
    const [question, setQuestion] = useState('');
    const [fullAnswer, setFullAnswer] = useState('');
    const [displayedAnswer, setDisplayedAnswer] = useState('');
    
    // 🔥 Track match count for real-time feedback
    const [matchCount, setMatchCount] = useState(0);
    
    // Set correct default agent once auth loads
    useEffect(() => {
        if (sportType === 'basketball') {
            setAgent('basketball');
        } else {
            // Keep current football agent if already set to hrhr, otherwise use default
            if (agent !== 'hrhr') {
                setAgent(getDefaultAgent());
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIapPremium, isPaidPremium, sportType]);

    const [status, setStatus] = useState('idle');
    const [loadingStage, setLoadingStage] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [isShareOpen, setIsShareOpen] = useState(false);

    // Usage tracking (per agent)
    const [dailyUsageV75, setDailyUsageV75] = useState(0);
    const [dailyUsageV10, setDailyUsageV10] = useState(0);
    const [weeklyUsageV10, setWeeklyUsageV10] = useState(0);
    // 🏀 Basketball usage tracking
    const [dailyUsageBB, setDailyUsageBB] = useState(0);
    // 🎯 NEW: HRHR usage tracking
    const [dailyUsageHRHR, setDailyUsageHRHR] = useState(0);
    const [isCheckingUsage, setIsCheckingUsage] = useState(true);

    // History
    const [searchHistory, setSearchHistory] = useState([]);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [expandedHistoryId, setExpandedHistoryId] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isDeletingHistory, setIsDeletingHistory] = useState(false);

    // =======================================================
    // DETERMINE USER TIER
    // =======================================================
    const getUserTier = () => {
        if (isIOS && isIapPremium) return 'PREMIUM';
        if (isTrialActive && profile?.plan === 'trial') return 'TRIAL';
        if (profile?.plan === 'premium') return 'PREMIUM';
        if (isPaidPremium && profile?.plan !== 'trial') return 'PREMIUM';
        if (isTrialActive) return 'TRIAL';
        return 'FREE';
    };

    const userTier = getUserTier();

    // Current engine limits based on agent + tier
    const currentLimits = ENGINE_LIMITS[agent]?.[userTier] || { searchesPerDay: 0, searchesPerWeek: 0, matches: 0 };
    
    // 🔥 UPDATED: Get the correct daily usage for current agent
    const getDailyUsage = () => {
        if (agent === 'v7_5') return dailyUsageV75;
        if (agent === 'v10') return dailyUsageV10;
        if (agent === 'basketball') return dailyUsageBB;
        if (agent === 'hrhr') return dailyUsageHRHR;
        return 0;
    };
    
    const dailyUsage = getDailyUsage();

    // =======================================================
    // 🔥 COUNT MATCHES (Real-time)
    // =======================================================
    const countMatches = (text) => {
        const t = String(text || '').replace(/\r/g, '\n').trim();
        if (!t) return 0;
        const lines = t.split('\n')
            .map((l) => l.trim())
            .filter(line => line.length > 0 && !line.startsWith('#') && !line.startsWith('//'));
        return lines.length <= 1 ? (t.length > 0 ? 1 : 0) : lines.length;
    };

    // 🔥 Update match count when question changes
    useEffect(() => {
        setMatchCount(countMatches(question));
    }, [question]);

    // 🔥 Check if match limit exceeded
    const isMatchLimitExceeded = matchCount > currentLimits.matches;

    // =======================================================
    // LOAD DAILY & WEEKLY USAGE (UTC - Matches Backend)
    // =======================================================
    useEffect(() => {
        const today = getUtcDayKey();        // UTC date key (matches backend)
        const weekStart = getUtcWeekKey();   // UTC week key (matches backend)
        const uid = user?.uid || 'guest';

        // V7.5 usage (daily only)
        const v75Key = `ai_v75_usage_${uid}`;
        const v75Stored = JSON.parse(localStorage.getItem(v75Key) || '{}');
        if (v75Stored.date !== today) {
            setDailyUsageV75(0);
            localStorage.setItem(v75Key, JSON.stringify({ date: today, count: 0 }));
        } else {
            setDailyUsageV75(v75Stored.count || 0);
        }

        // V10 daily usage
        const v10DayKey = `ai_v10_daily_${uid}`;
        const v10DayStored = JSON.parse(localStorage.getItem(v10DayKey) || '{}');
        if (v10DayStored.date !== today) {
            setDailyUsageV10(0);
            localStorage.setItem(v10DayKey, JSON.stringify({ date: today, count: 0 }));
        } else {
            setDailyUsageV10(v10DayStored.count || 0);
        }

        // V10 weekly usage
        const v10WeekKey = `ai_v10_weekly_${uid}`;
        const v10WeekStored = JSON.parse(localStorage.getItem(v10WeekKey) || '{}');
        if (v10WeekStored.weekStart !== weekStart) {
            setWeeklyUsageV10(0);
            localStorage.setItem(v10WeekKey, JSON.stringify({ weekStart: weekStart, count: 0 }));
        } else {
            setWeeklyUsageV10(v10WeekStored.count || 0);
        }

        // 🏀 Basketball usage (daily only)
        const bbKey = `ai_bb_usage_${uid}`;
        const bbStored = JSON.parse(localStorage.getItem(bbKey) || '{}');
        if (bbStored.date !== today) {
            setDailyUsageBB(0);
            localStorage.setItem(bbKey, JSON.stringify({ date: today, count: 0 }));
        } else {
            setDailyUsageBB(bbStored.count || 0);
        }

        // 🎯 NEW: HRHR usage (daily only)
        const hrhrKey = `ai_hrhr_usage_${uid}`;
        const hrhrStored = JSON.parse(localStorage.getItem(hrhrKey) || '{}');
        if (hrhrStored.date !== today) {
            setDailyUsageHRHR(0);
            localStorage.setItem(hrhrKey, JSON.stringify({ date: today, count: 0 }));
        } else {
            setDailyUsageHRHR(hrhrStored.count || 0);
        }

        setIsCheckingUsage(false);
    }, [user?.uid]);

    // =======================================================
    // INCREMENT USAGE (per agent) - UTC Dates
    // =======================================================
    const incrementUsage = () => {
        const today = getUtcDayKey();        // UTC date key (matches backend)
        const weekStart = getUtcWeekKey();   // UTC week key (matches backend)
        const uid = user?.uid || 'guest';

        if (agent === 'v7_5') {
            const newCount = dailyUsageV75 + 1;
            setDailyUsageV75(newCount);
            localStorage.setItem(`ai_v75_usage_${uid}`, JSON.stringify({ date: today, count: newCount }));
        } else if (agent === 'v10') {
            // V10: Increment both daily and weekly
            const newDailyCount = dailyUsageV10 + 1;
            const newWeeklyCount = weeklyUsageV10 + 1;
            
            setDailyUsageV10(newDailyCount);
            setWeeklyUsageV10(newWeeklyCount);
            
            localStorage.setItem(`ai_v10_daily_${uid}`, JSON.stringify({ date: today, count: newDailyCount }));
            localStorage.setItem(`ai_v10_weekly_${uid}`, JSON.stringify({ weekStart: weekStart, count: newWeeklyCount }));
        } else if (agent === 'basketball') {
            // 🏀 Basketball: Daily only
            const newCount = dailyUsageBB + 1;
            setDailyUsageBB(newCount);
            localStorage.setItem(`ai_bb_usage_${uid}`, JSON.stringify({ date: today, count: newCount }));
        } else if (agent === 'hrhr') {
            // 🎯 HRHR: Daily only
            const newCount = dailyUsageHRHR + 1;
            setDailyUsageHRHR(newCount);
            localStorage.setItem(`ai_hrhr_usage_${uid}`, JSON.stringify({ date: today, count: newCount }));
        }
    };

    // =======================================================
    // CHECK IF USER CAN SEARCH
    // =======================================================
    const canSearch = () => {
        // V10 requires trial/premium
        if (agent === 'v10' && userTier === 'FREE') return false;
        
        // 🏀 Basketball requires Premium only
        if (agent === 'basketball' && userTier !== 'PREMIUM') return false;

        // 🎯 HRHR requires Premium only
        if (agent === 'hrhr' && userTier !== 'PREMIUM') return false;
        
        // Check daily limit
        if (dailyUsage >= currentLimits.searchesPerDay) return false;
        
        // Check weekly limit (V10 Premium only)
        if (agent === 'v10' && userTier === 'PREMIUM' && currentLimits.searchesPerWeek) {
            if (weeklyUsageV10 >= currentLimits.searchesPerWeek) return false;
        }
        
        return true;
    };

    // 🔥 Get the limiting factor for display
    const getLimitReason = () => {
        if (agent === 'v10' && userTier === 'PREMIUM') {
            if (weeklyUsageV10 >= currentLimits.searchesPerWeek) return 'weekly';
            if (dailyUsageV10 >= currentLimits.searchesPerDay) return 'daily';
        }
        if (dailyUsage >= currentLimits.searchesPerDay) return 'daily';
        return null;
    };

    // =======================================================
    // HISTORY FUNCTIONS
    // =======================================================
    const fetchHistory = async () => {
        if (!user?.uid) return;
        setIsLoadingHistory(true);
        try {
            const historyRef = collection(db, 'users', user.uid, 'prediction_history');
            const q = query(historyRef, orderBy('createdAt', 'desc'), limit(MAX_HISTORY_ITEMS));
            const snapshot = await getDocs(q);
            const items = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate() || new Date()
            }));
            setSearchHistory(items);
        } catch (err) {
            console.error('[History] Failed to fetch:', err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [user?.uid]);

    const saveToHistory = async (questionText, answerText, agentType) => {
        if (!user?.uid || !questionText || !answerText) return;
        try {
            const historyRef = collection(db, 'users', user.uid, 'prediction_history');
            await addDoc(historyRef, {
                question: questionText,
                answer: answerText,
                agent: agentType,
                sport: agentType === 'basketball' ? 'basketball' : 'football',
                result: 'pending', // 📊 Default result for Prediction History tracking
                createdAt: serverTimestamp()
            });
            // History items are now kept permanently for the Prediction History page
            await fetchHistory();
        } catch (err) {
            console.error('[History] Failed to save:', err);
        }
    };

    const deleteHistoryItem = async (itemId, e) => {
        e.stopPropagation();
        if (!user?.uid || !itemId) return;
        try {
            await deleteDoc(doc(db, 'users', user.uid, 'prediction_history', itemId));
            setSearchHistory((prev) => prev.filter((item) => item.id !== itemId));
            if (expandedHistoryId === itemId) setExpandedHistoryId(null);
        } catch (err) { console.error(err); }
    };

    const clearAllHistory = async () => {
        if (!user?.uid || searchHistory.length === 0) return;
        if (!window.confirm('Clear all history?')) return;
        setIsDeletingHistory(true);
        try {
            const historyRef = collection(db, 'users', user.uid, 'prediction_history');
            const snapshot = await getDocs(historyRef);
            const batch = writeBatch(db);
            snapshot.docs.forEach((docSnap) => {
                batch.delete(doc(db, 'users', user.uid, 'prediction_history', docSnap.id));
            });
            await batch.commit();
            setSearchHistory([]);
            setExpandedHistoryId(null);
        } catch (err) { console.error(err); }
        finally { setIsDeletingHistory(false); }
    };

    const loadFromHistory = (historyItem) => {
        setQuestion(historyItem.question);
        setFullAnswer(historyItem.answer);
        setDisplayedAnswer(historyItem.answer);
        
        // 🏀 Handle basketball history items
        if (historyItem.agent === 'basketball' || historyItem.sport === 'basketball') {
            setSportType('basketball');
            setAgent('basketball');
        } else if (historyItem.agent === 'hrhr') {
            // 🎯 Handle HRHR history items
            setSportType('football');
            setAgent('hrhr');
        } else {
            setSportType('football');
            const agentFromHistory = historyItem.agent === 'v10' ? 'v10' : 'v7_5';
            setAgent(agentFromHistory);
        }
        
        setStatus('idle');
        setIsHistoryOpen(false);
        setExpandedHistoryId(null);
        setTimeout(() => {
            if (answerRef.current) answerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    // =======================================================
    // HELPERS
    // =======================================================
    const formatTimeAgo = (date) => {
        if (!date) return '';
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMs / 3600000);
        if (diffHours < 24) return `${diffHours}h ago`;
        return date.toLocaleDateString();
    };

    // 🔥 Loading stages (use appropriate stages for each agent)
    const getActiveLoadingStages = () => {
        if (agent === 'basketball') return BASKETBALL_LOADING_STAGES;
        if (agent === 'hrhr') return HRHR_LOADING_STAGES;
        return LOADING_STAGES;
    };
    const activeLoadingStages = getActiveLoadingStages();

    // Loading stages effect
    useEffect(() => {
        if (status !== 'loading') {
            setLoadingStage(0);
            return;
        }
        setLoadingStage(0);
        const timers = [];
        let accumulatedTime = 0;
        activeLoadingStages.forEach((stage, index) => {
            if (index === 0) return;
            accumulatedTime += activeLoadingStages[index - 1].duration || 0;
            if (activeLoadingStages[index - 1].duration) {
                const timer = setTimeout(() => setLoadingStage(index), accumulatedTime);
                timers.push(timer);
            }
        });
        return () => timers.forEach((timer) => clearTimeout(timer));
    }, [status, agent]);

    // Typewriter effect
    useEffect(() => {
        if (status !== 'typing' || !fullAnswer) return;
        let i = 0;
        setDisplayedAnswer('');
        const intervalId = setInterval(() => {
            setDisplayedAnswer((prev) => prev + fullAnswer.charAt(i));
            i += 1;
            if (i >= fullAnswer.length) {
                clearInterval(intervalId);
                setStatus('idle');
            }
        }, 10);
        return () => clearInterval(intervalId);
    }, [status, fullAnswer]);

    // Share
    const getSharePayload = () => {
        const url = window.location.href;
        // 🔥 Updated engine label
        const engineLabel = agent === 'basketball' 
            ? 'AiPredict Basketball V4' 
            : agent === 'hrhr'
                ? 'AiPredict HRHR V5.5'
                : agent === 'v7_5' 
                    ? 'AiPredict V7.5' 
                    : 'AiPredict V10.0';
        const safeQuestion = String(question || '').trim();
        const safeAnswer = String(fullAnswer || displayedAnswer || '').trim();
        const blurb = safeAnswer ? safeAnswer.slice(0, 220).replace(/\s+/g, ' ').trim() : '';
        const baseText = safeQuestion ? `${engineLabel} analysis for: ${safeQuestion}` : `${engineLabel} analysis`;
        const text = blurb ? `${baseText}\n\n${blurb}` : baseText;
        return { url, text };
    };

    const openShare = (platform) => {
        const { url, text } = getSharePayload();
        const encUrl = encodeURIComponent(url);
        const encText = encodeURIComponent(text);

        if (platform === 'twitter') {
            window.open(`https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`, '_blank');
        } else if (platform === 'copy') {
            navigator.clipboard.writeText(url).catch(() => { });
            setIsShareOpen(false);
        }
    };

    // =======================================================
    // 🏀 HANDLE SPORT TYPE SWITCH
    // =======================================================
    const handleSportSwitch = (newSport) => {
        if (newSport === sportType) return;
        setSportType(newSport);
        setFullAnswer('');
        setDisplayedAnswer('');
        setErrorMessage('');
        setStatus('idle');
        setIsShareOpen(false);
        setQuestion('');
        
        // Set appropriate agent for new sport
        if (newSport === 'basketball') {
            setAgent('basketball');
        } else {
            setAgent(getDefaultAgent());
        }
    };

    // =======================================================
    // HANDLE ENGINE SWITCH (Football only)
    // =======================================================
    const handleEngineSwitch = (newAgent) => {
        if (newAgent === agent) return;
        setAgent(newAgent);
        setFullAnswer('');
        setDisplayedAnswer('');
        setErrorMessage('');
        setStatus('idle');
        setIsShareOpen(false);
    };

    // =======================================================
    // HANDLE START TRIAL
    // =======================================================
    const handleStartTrial = () => {
        if (!user) {
            navigate('/signin');
            return;
        }
        
        if (isIOS) {
            navigate('/pricing');
            return;
        }
        
        let checkoutUrl = WHOP_TRIAL_URL;
        if (user?.email) {
            const separator = checkoutUrl.includes('?') ? '&' : '?';
            checkoutUrl = `${checkoutUrl}${separator}email=${encodeURIComponent(user.email)}`;
        }
        window.location.href = checkoutUrl;
    };

    // =======================================================
    // MAIN HANDLE ASK
    // =======================================================
    const handleAsk = async (e) => {
        e.preventDefault();
        if (!user || !question.trim()) return;

        // Gate: V10 requires trial/premium
        if (agent === 'v10' && userTier === 'FREE') {
            setErrorMessage('V10.0 requires Trial or Premium. Switch to V7.5 (Free) or start your trial.');
            return;
        }

        // 🏀 Gate: Basketball requires Premium only
        if (agent === 'basketball' && userTier !== 'PREMIUM') {
            setErrorMessage('🏀 Basketball V4 is exclusive to Premium subscribers.');
            return;
        }

        // 🎯 Gate: HRHR requires Premium only
        if (agent === 'hrhr' && userTier !== 'PREMIUM') {
            setErrorMessage('🎯 HRHR V5.5 is exclusive to Premium subscribers.');
            return;
        }

        // Check limits
        if (!canSearch()) {
            const limitReason = getLimitReason();
            if (limitReason === 'weekly') {
                setErrorMessage(`Weekly limit reached (${currentLimits.searchesPerWeek}/week). Resets every Monday.`);
            } else {
                setErrorMessage(`Daily limit reached (${currentLimits.searchesPerDay}/day). Come back tomorrow!`);
            }
            return;
        }

        // 🔥 Match count validation (frontend block)
        if (matchCount > currentLimits.matches) {
            const matchLabel = agent === 'basketball' ? 'games' : 'matches';
            setErrorMessage(`Maximum ${currentLimits.matches} ${matchLabel} allowed per search. You entered ${matchCount}. ${userTier === 'FREE' ? 'Upgrade to Premium for more!' : 'Please reduce your list.'}`);
            return;
        }

        const currentQuestion = question.trim();
        const currentAgent = agent;

        setStatus('loading');
        setLoadingStage(0);
        setErrorMessage('');
        setFullAnswer('');
        setDisplayedAnswer('');
        setIsShareOpen(false);

        try {
            const functions = getFunctions(app, 'us-central1');

            // 🔥 Select correct function based on agent
            let fnToCall;
            if (currentAgent === 'basketball') {
                fnToCall = httpsCallable(functions, 'getBasketballAiPredictionV1', { timeout: 540000 });
           
            } else if (currentAgent === 'hrhr') {
                // 🎯 NEW: HRHR function
                fnToCall = httpsCallable(functions, 'getFootballAiPredictionHRHR', { timeout: 540000 });

            } else if (currentAgent === 'v7_5') {
                fnToCall = httpsCallable(functions, 'getFootballAiPredictionV2', { timeout: 540000 });
            } else {
                fnToCall = httpsCallable(functions, 'getFootballAiPredictionV10', { timeout: 540000 });
            }

            const res = await fnToCall({ question });
            const data = res.data || {};
            let answerText = String(data.answer || '');

            // Check if backend returned a limit error
            if (data.limitReached || data.matchLimitExceeded) {
                setErrorMessage(answerText || 'Limit reached.');
                setStatus('idle');
                return;
            }

            // 🔒 Sanitize HRHR output to protect proprietary V5.5 methodology
            // This removes BLACKLIST/CRISIS CHECK, RULES TRIGGERED, FILTERS APPLIED,
            // REJECTED OPTIONS, and other internal methodology markers
            if (currentAgent === 'hrhr') {
                answerText = sanitizeHRHROutput(answerText);
            }

            setFullAnswer(answerText);
            setStatus('typing');

            // Increment usage on success
            incrementUsage();

            if (answerText && !answerText.startsWith('❌')) {
                await saveToHistory(currentQuestion, answerText, currentAgent);
                
                // 🌟 Trigger rating prompt after successful prediction
                onPredictionSuccess();
            }
        } catch (err) {
            console.error('AiAssistant error:', err);
            setStatus('error');
            let msg = 'Something went wrong. Please try again.';
            if (err.code === 'functions/resource-exhausted') {
                msg = err.message || 'Limit reached.';
            } else if (err.code === 'functions/unauthenticated') {
                msg = 'Please sign in again to use AiPredict.';
            } else if (err.code === 'functions/permission-denied') {
                msg = err.message || 'Access denied. Premium subscription required.';
            } else if (err.code === 'functions/deadline-exceeded') {
                msg = '⏳ The analysis is taking longer than usual. Try asking for fewer matches.';
            } else if (err.message) {
                msg = err.message;
            }
            setErrorMessage(msg);
        }
    };

    // 🔥 UPDATED: Disable input if match limit exceeded
    const isInputDisabled = status === 'loading' || status === 'typing' || !canSearch();
    const isSubmitDisabled = isInputDisabled || !question.trim() || !user || isMatchLimitExceeded;

    // =======================================================
    // 🏀 RENDER: SPORT TOGGLE
    // =======================================================
    const renderSportToggle = () => {
        return (
            <div style={{
                display: 'flex',
                gap: '0',
                marginBottom: '1rem',
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '12px',
                padding: '4px',
                border: '1px solid rgba(148, 163, 184, 0.1)'
            }}>
                <button
                    type="button"
                    onClick={() => handleSportSwitch('football')}
                    disabled={status === 'loading'}
                    style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        background: sportType === 'football' 
                            ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' 
                            : 'transparent',
                        color: sportType === 'football' ? '#fff' : '#94a3b8'
                    }}
                >
                    <Icon icon="solar:football-bold" style={{ fontSize: '1.1rem' }} />
                    Football
                </button>
                <button
                    type="button"
                    onClick={() => handleSportSwitch('basketball')}
                    disabled={status === 'loading'}
                    style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        background: sportType === 'basketball' 
                            ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' 
                            : 'transparent',
                        color: sportType === 'basketball' ? '#fff' : '#94a3b8'
                    }}
                >
                    <Icon icon="solar:basketball-bold" style={{ fontSize: '1.1rem' }} />
                    Basketball
                </button>
            </div>
        );
    };

    // =======================================================
    // RENDER: USAGE PILL
    // =======================================================
    const renderUsagePill = () => {
        if (isCheckingUsage) {
            return <div className="aiassist__usage-skeleton" />;
        }

        const tierLabel = userTier === 'PREMIUM' ? 'Premium' : userTier === 'TRIAL' ? 'Trial' : 'Free';
        const tierColor = userTier === 'PREMIUM' ? '#4ade80' : userTier === 'TRIAL' ? '#fbbf24' : '#60a5fa';
        
        // 🔥 Engine label based on agent
        const engineLabel = agent === 'basketball' 
            ? '🏀 V4' 
            : agent === 'hrhr'
                ? '🎯 HRHR'
                : agent === 'v7_5' 
                    ? 'V7.5' 
                    : 'V10';

        // Show both daily and weekly for V10 Premium
        const showWeekly = agent === 'v10' && userTier === 'PREMIUM' && currentLimits.searchesPerWeek;

        return (
            <div className="aiassist__usage">
                <span style={{
                    background: tierColor + '20',
                    color: tierColor,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    marginRight: '8px'
                }}>
                    {tierLabel}
                </span>
                <span>{engineLabel}:</span>
                <strong style={{ marginLeft: '6px', color: tierColor }}>
                    {dailyUsage}/{currentLimits.searchesPerDay} today
                </strong>
                {showWeekly && (
                    <strong style={{ marginLeft: '8px', color: weeklyUsageV10 >= 25 ? '#fbbf24' : '#94a3b8' }}>
                        • {weeklyUsageV10}/{currentLimits.searchesPerWeek} this week
                    </strong>
                )}
                <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '0.8rem' }}>
                    (max {currentLimits.matches} {agent === 'basketball' ? 'games' : 'matches'})
                </span>
            </div>
        );
    };

    // =======================================================
    // 🔥 RENDER MATCH COUNTER
    // =======================================================
    const renderMatchCounter = () => {
        if (!question.trim()) return null;
        
        const isOver = matchCount > currentLimits.matches;
        const color = isOver ? '#ef4444' : matchCount === currentLimits.matches ? '#fbbf24' : '#4ade80';
        const matchLabel = agent === 'basketball' ? 'Games' : 'Matches';
        
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: isOver ? 'rgba(239, 68, 68, 0.1)' : 'rgba(30, 41, 59, 0.5)',
                borderRadius: '8px',
                marginBottom: '8px',
                border: isOver ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid transparent'
            }}>
                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    {matchLabel} detected:
                </span>
                <span style={{ 
                    color: color, 
                    fontWeight: '600',
                    fontSize: '0.9rem'
                }}>
                    {matchCount}/{currentLimits.matches}
                    {isOver && ' ⚠️ Too many!'}
                </span>
            </div>
        );
    };

    // =======================================================
    // RENDER: LOADING STATUS
    // =======================================================
    const renderLoadingStatus = () => {
        const currentStage = activeLoadingStages[loadingStage] || activeLoadingStages[0];
        return (
            <div className="aiassist__loading-status">
                <div className="aiassist__loading-icon">
                    <div className="aiassist__spinner" />
                </div>
                <div className="aiassist__loading-content">
                    <p className="aiassist__loading-text">{currentStage.text}</p>
                    <div className="aiassist__loading-progress">
                        {activeLoadingStages.map((_, index) => (
                            <span key={index} className={`aiassist__loading-dot ${index <= loadingStage ? 'active' : ''}`} />
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // =======================================================
    // RENDER: HISTORY PANEL
    // =======================================================
    const renderHistoryPanel = () => {
        if (!user) return null;
        return (
            <div className="aiassist__history">
                <button type="button" className="aiassist__history-toggle" onClick={() => setIsHistoryOpen(!isHistoryOpen)}>
                    <span className="aiassist__history-icon">📋</span>
                    <span>Recent Searches ({searchHistory.length})</span>
                    <span className={`aiassist__history-arrow ${isHistoryOpen ? 'open' : ''}`}>▼</span>
                </button>

                {isHistoryOpen && (
                    <div className="aiassist__history-list">
                        <div className="aiassist__history-actions">
                            <button 
                                type="button" 
                                className="aiassist__upgrade-btn" 
                                onClick={() => navigate('/prediction-history')}
                                style={{ 
                                    padding: '8px 14px', 
                                    fontSize: '0.82rem',
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                📊 Full History & Stats
                            </button>
                            {searchHistory.length > 0 && (
                                <button type="button" className="aiassist__history-clear-all" onClick={clearAllHistory} disabled={isDeletingHistory}>
                                    {isDeletingHistory ? 'Clearing...' : '🗑️ Clear All'}
                                </button>
                            )}
                        </div>

                        {isLoadingHistory ? (
                            <div className="aiassist__history-loading">Loading history...</div>
                        ) : searchHistory.length === 0 ? (
                            <div className="aiassist__history-empty">No recent searches yet</div>
                        ) : (
                            searchHistory.map((item) => (
                                <div key={item.id} className="aiassist__history-item">
                                    <div className="aiassist__history-header" onClick={() => setExpandedHistoryId(expandedHistoryId === item.id ? null : item.id)}>
                                        <div className="aiassist__history-meta">
                                            {/* 🔥 Updated history agent badge */}
                                            <span className="aiassist__history-agent" style={{
                                                background: (item.agent === 'basketball' || item.sport === 'basketball') 
                                                    ? 'rgba(249, 115, 22, 0.2)' 
                                                    : item.agent === 'hrhr'
                                                        ? 'rgba(16, 185, 129, 0.2)'
                                                        : undefined,
                                                color: (item.agent === 'basketball' || item.sport === 'basketball')
                                                    ? '#f97316'
                                                    : item.agent === 'hrhr'
                                                        ? '#10b981'
                                                        : undefined
                                            }}>
                                                {(item.agent === 'basketball' || item.sport === 'basketball') 
                                                    ? '🏀 V4' 
                                                    : item.agent === 'hrhr'
                                                        ? '🎯 HRHR'
                                                        : item.agent === 'v10' 
                                                            ? 'V10.0' 
                                                            : 'V7.5'
                                                }
                                            </span>
                                            <span className="aiassist__history-time">{formatTimeAgo(item.createdAt)}</span>
                                        </div>
                                        <p className="aiassist__history-question">
                                            {item.question.length > 60 ? item.question.substring(0, 60) + '...' : item.question}
                                        </p>
                                        <button type="button" className="aiassist__history-delete" onClick={(e) => deleteHistoryItem(item.id, e)} title="Delete">🗑️</button>
                                        <span className={`aiassist__history-expand ${expandedHistoryId === item.id ? 'open' : ''}`}>▼</span>
                                    </div>

                                    {expandedHistoryId === item.id && (
                                        <div className="aiassist__history-preview">
                                            <div className="aiassist__history-answer">
                                                <ReactMarkdown>{item.answer.length > 500 ? item.answer.substring(0, 500) + '...' : item.answer}</ReactMarkdown>
                                            </div>
                                            <button type="button" className="aiassist__history-load" onClick={() => loadFromHistory(item)}>View Full Analysis</button>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    };

    // =======================================================
    // 🏀 RENDER: BASKETBALL PREMIUM GATE
    // =======================================================
    const renderBasketballGate = () => {
        if (sportType !== 'basketball') return null;
        if (userTier === 'PREMIUM') return null;

        return (
            <div style={{
                background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(234, 88, 12, 0.1) 100%)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
            }}>
                <Icon icon="solar:basketball-bold" style={{ fontSize: '3rem', color: '#f97316', marginBottom: '1rem' }} />
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>🏀 Basketball V4</h3>
                <p style={{ color: '#f97316', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Premium Exclusive Feature
                </p>
                <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Our new basketball prediction engine with H2H analysis, fatigue detection, and NBA/European league support.
                    <br />
                    <strong style={{ color: '#fff' }}>3 searches/day • Max 2 games per search</strong>
                </p>

                <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    style={{
                        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                        color: '#fff',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Icon icon="solar:crown-bold" />
                    Upgrade to Premium
                </button>

                <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '1rem' }}>
                    🔒 Premium subscribers get access to all sports engines
                </p>
            </div>
        );
    };

    // =======================================================
    // 🎯 RENDER: HRHR PREMIUM GATE - 🔄 UPDATED LIMITS
    // =======================================================
    const renderHRHRGate = () => {
        if (sportType !== 'football') return null;
        if (agent !== 'hrhr') return null;
        if (userTier === 'PREMIUM') return null;

        return (
            <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
            }}>
                <Icon icon="solar:target-bold" style={{ fontSize: '3rem', color: '#10b981', marginBottom: '1rem' }} />
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>🎯 HRHR V5.5</h3>
                <p style={{ color: '#10b981', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Premium Exclusive Feature
                </p>
                <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    High Risk High Reward value picks. Identifies mispriced markets where true probability exceeds bookmaker odds.
                    <br />
                    <strong style={{ color: '#fff' }}>3 searches/day • Max 3 matches per search</strong>
                </p>

                <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#fff',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Icon icon="solar:crown-bold" />
                    Upgrade to Premium
                </button>

                <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '1rem' }}>
                    🔒 Premium subscribers get access to all prediction engines
                </p>
            </div>
        );
    };

    // =======================================================
    // RENDER: FREE USER GATE (only when on V10)
    // =======================================================
    const renderFreeUserGate = () => {
        if (userTier !== 'FREE') return null;
        if (agent !== 'v10') return null;
        if (sportType === 'basketball') return null; // Handled by basketball gate

        const showSubscribeOnly = isIOS || hasUsedTrial;

        return (
            <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
            }}>
                <Icon icon="solar:lock-keyhole-bold" style={{ fontSize: '2.5rem', color: '#ef4444', marginBottom: '1rem' }} />
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>V10.0 Engine Requires Access</h3>
                <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    {showSubscribeOnly
                        ? "Subscribe to Premium to unlock V10.0 with 4 searches/day."
                        : "Start your 3-day free trial to access the V10.0 prediction engine. Credit card required, cancel anytime."
                    }
                </p>

                {showSubscribeOnly ? (
                    <button
                        type="button"
                        onClick={() => navigate('/pricing')}
                        style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            color: '#fff',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Icon icon="solar:crown-bold" />
                        Subscribe to Premium
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleStartTrial}
                        style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            color: '#fff',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Icon icon="solar:play-bold" />
                        Start 3-Day Free Trial
                    </button>
                )}

                <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '1rem' }}>
                    {showSubscribeOnly 
                        ? "🔒 Secure payment • Cancel anytime"
                        : "💳 Credit card required • Cancel anytime • No charge for 3 days"
                    }
                </p>
            </div>
        );
    };

    // =======================================================
    // RENDER: TRIAL USER INFO BANNER
    // =======================================================
    const renderTrialBanner = () => {
        if (userTier !== 'TRIAL') return null;
        if (agent !== 'v10') return null;

        return (
            <div style={{
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.5rem'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="solar:clock-circle-bold" style={{ color: '#fbbf24' }} />
                    <span style={{ color: '#fbbf24', fontSize: '0.9rem' }}>
                        Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left • 1 search/day on V10
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    style={{
                        background: '#fbbf24',
                        color: '#000',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600'
                    }}
                >
                    Upgrade to 4/day
                </button>
            </div>
        );
    };

    // =======================================================
    // RENDER: LIMIT REACHED MESSAGE - 🔄 UPDATED HRHR TEXT
    // =======================================================
    const renderLimitReached = () => {
        if (userTier === 'FREE' && agent === 'v10') return null;
        if (sportType === 'basketball' && userTier !== 'PREMIUM') return null;
        if (agent === 'hrhr' && userTier !== 'PREMIUM') return null;
        if (canSearch()) return null;

        const limitReason = getLimitReason();

        return (
            <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                textAlign: 'center'
            }}>
                <p style={{ color: '#ef4444', marginBottom: '0.5rem' }}>
                    {limitReason === 'weekly' 
                        ? `⚠️ Weekly limit reached (${weeklyUsageV10}/${currentLimits.searchesPerWeek})`
                        : `⚠️ Daily limit reached (${dailyUsage}/${currentLimits.searchesPerDay})`
                    }
                </p>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>
                    {limitReason === 'weekly'
                        ? "Your weekly searches reset every Monday. Try V7.5 for unlimited basic analysis!"
                        : agent === 'v10' && userTier === 'TRIAL'
                            ? "Trial users get 1 search per day on V10. Upgrade to Premium for 4/day!"
                            : agent === 'basketball'
                                ? "Basketball V4: 3 searches per day. Come back tomorrow!"
                                : agent === 'hrhr'
                                    ? "HRHR V5.5: 3 searches per day. Come back tomorrow!"
                                    : agent === 'v7_5'
                                        ? "You've used your 3 free V7.5 searches for today. Come back tomorrow!"
                                        : "Come back tomorrow for more searches!"
                    }
                </p>
                {(agent === 'v10' && userTier === 'TRIAL') && (
                    <button
                        type="button"
                        onClick={() => navigate('/pricing')}
                        style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            color: '#fff',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600'
                        }}
                    >
                        Upgrade to Premium
                    </button>
                )}
            </div>
        );
    };

    // =======================================================
    // 🔥 GET PLACEHOLDER TEXT
    // =======================================================
    const getPlaceholderText = () => {
        if (!user) return 'Sign in to ask...';
        
        if (agent === 'basketball') {
            return `🏀 Basketball V4 Example:\n1. Lakers vs Celtics\n2. Real Madrid vs Barcelona\n(Max ${currentLimits.matches} games)`;
        }

        if (agent === 'hrhr') {
            return `🎯 HRHR V5.5 Example:\n1. Liverpool v Man City\n2. Real Madrid v Barcelona\n(Max ${currentLimits.matches} matches - Value picks only)`;
        }
        
        return `${agent === 'v7_5' ? 'V7.5' : 'V10.0'} Example:\n1. Liverpool v Man City\n2. Real Madrid v Barcelona\n(Max ${currentLimits.matches} matches)`;
    };

    // =======================================================
    // CHECK IF FORM SHOULD BE SHOWN
    // =======================================================
    const shouldShowForm = () => {
        // Basketball: Premium only
        if (sportType === 'basketball') {
            return userTier === 'PREMIUM' && canSearch();
        }
        // HRHR: Premium only
        if (agent === 'hrhr') {
            return userTier === 'PREMIUM' && canSearch();
        }
        // Football V7.5: Everyone
        if (agent === 'v7_5') {
            return canSearch();
        }
        // Football V10: Trial or Premium
        if (agent === 'v10') {
            return userTier !== 'FREE' && canSearch();
        }
        return false;
    };

    // =======================================================
    // 🔥 GET ENGINE DISPLAY NAME
    // =======================================================
    const getEngineDisplayName = () => {
        if (agent === 'basketball') return '🏀 V4 Live';
        if (agent === 'hrhr') return '🎯 HRHR Live';
        if (agent === 'v7_5') return 'V7.5 Live';
        return 'V10.0 Live';
    };

    const getEngineModelName = () => {
        if (agent === 'basketball') return 'V4';
        if (agent === 'hrhr') return 'HRHR V5.5';
        if (agent === 'v7_5') return 'V7.5 Model';
        return 'V10.0 Model';
    };

    // =======================================================
    // MAIN RENDER
    // =======================================================
    return (
        <section className="aiassist">
            <div className="aiassist__inner">
                {/* LEFT */}
                <div className="aiassist__left">
                    <div className="aiassist__eyebrow">
                        <span className="aiassist__dot" />
                        <span>
                            AiPredict • {sportType === 'basketball' 
                                ? '🏀 Basketball V4' 
                                : agent === 'hrhr'
                                    ? '🎯 HRHR V5.5'
                                    : userTier === 'PREMIUM' 
                                        ? 'V10.0 Premium' 
                                        : agent === 'v7_5' 
                                            ? 'V7.5 Model' 
                                            : 'V10.0 Model'
                            }
                        </span>
                    </div>

                    <h1 className="aiassist__title">
                        {sportType === 'basketball' 
                            ? 'Ask AiPredict for basketball games' 
                            : agent === 'hrhr'
                                ? 'Find value picks with HRHR'
                                : 'Ask AiPredict for football matches'
                        }
                    </h1>

                    {/* 🔄 UPDATED: Subtitle text with new limits */}
                    <p className="aiassist__subtitle">
                        {sportType === 'basketball' && userTier === 'PREMIUM' && (
                            <>🏀 Basketball V4: <strong>3 searches/day</strong>, up to 2 games. H2H analysis.</>
                        )}
                        {sportType === 'basketball' && userTier !== 'PREMIUM' && (
                            <>🏀 Basketball V4 is exclusive to <strong>Premium subscribers</strong>.</>
                        )}
                        {sportType === 'football' && agent === 'hrhr' && userTier === 'PREMIUM' && (
                            <>🎯 HRHR V5.5: <strong>3 searches/day</strong>, up to 3 matches. Value edge analysis.</>
                        )}
                        {sportType === 'football' && agent === 'hrhr' && userTier !== 'PREMIUM' && (
                            <>🎯 HRHR V5.5 is exclusive to <strong>Premium subscribers</strong>.</>
                        )}
                        {sportType === 'football' && agent !== 'hrhr' && userTier === 'PREMIUM' && (
                            <>V10.0 (90% Accuracy): <strong>4 searches/day</strong>, up to 3 matches.</>
                        )}
                        {sportType === 'football' && agent !== 'hrhr' && userTier === 'TRIAL' && agent === 'v7_5' && (
                            <>V7.5 is <strong>free for everyone</strong>: 3 searches/day, up to 10 matches.</>
                        )}
                        {sportType === 'football' && agent !== 'hrhr' && userTier === 'TRIAL' && agent === 'v10' && (
                            <>V10.0 Trial: <strong>1 search/day</strong>, up to 3 matches. Upgrade for 4/day!</>
                        )}
                        {sportType === 'football' && agent !== 'hrhr' && userTier === 'FREE' && agent === 'v7_5' && (
                            <>V7.5 is <strong>free for everyone</strong>: 3 searches/day, up to <strong>2 matches</strong>. Upgrade for more!</>
                        )}
                        {sportType === 'football' && agent !== 'hrhr' && userTier === 'FREE' && agent === 'v10' && (
                            <>V10.0 requires Trial or Premium. Switch to V7.5 or subscribe.</>
                        )}
                    </p>

                    {/* Only show Bookmaker for football */}
                    {sportType === 'football' && <Bookmaker />}

                    {/* 🏀 Sport Toggle */}
                    {renderSportToggle()}

                    {/* 🔥 Engine toggle (Football only) - Updated with HRHR */}
                    {sportType === 'football' && (
                        <div style={{ display: 'flex', gap: '8px', margin: '0.75rem 0 1rem', flexWrap: 'wrap' }}>
                            {/* V7.5 Button - Always visible */}
                            <button
                                type="button"
                                className="aiassist__button"
                                onClick={() => handleEngineSwitch('v7_5')}
                                disabled={status === 'loading'}
                                style={{
                                    padding: '0.4rem 1rem',
                                    boxShadow: 'none',
                                    opacity: agent === 'v7_5' ? 1 : 0.6,
                                    background: agent === 'v7_5' ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' : 'rgba(71,85,105,0.3)'
                                }}
                            >
                                V7.5 {userTier === 'FREE' ? '(Free)' : ''}
                            </button>

                            {/* V10 Button - Always visible */}
                            <button
                                type="button"
                                className="aiassist__button"
                                onClick={() => handleEngineSwitch('v10')}
                                disabled={status === 'loading'}
                                style={{
                                    padding: '0.4rem 1rem',
                                    boxShadow: 'none',
                                    opacity: agent === 'v10' ? 1 : 0.6,
                                    background: agent === 'v10' ? 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' : 'rgba(71,85,105,0.3)'
                                }}
                            >
                                V10.0 {userTier === 'TRIAL' ? '(Trial)' : userTier === 'PREMIUM' ? '' : '🔒'}
                            </button>

                            {/* 🎯 HRHR Button - NEW */}
                            <button
                                type="button"
                                className="aiassist__button"
                                onClick={() => handleEngineSwitch('hrhr')}
                                disabled={status === 'loading'}
                                style={{
                                    padding: '0.4rem 1rem',
                                    boxShadow: 'none',
                                    opacity: agent === 'hrhr' ? 1 : 0.6,
                                    background: agent === 'hrhr' 
                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                        : 'rgba(71,85,105,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                🎯 HRHR
                                {userTier !== 'PREMIUM' && ' 🔒'}
                            </button>
                        </div>
                    )}

                    {renderUsagePill()}
                    {renderTrialBanner()}
                    {renderHistoryPanel()}

                    {/* 🏀 Basketball Premium Gate */}
                    {renderBasketballGate()}

                    {/* 🎯 HRHR Premium Gate */}
                    {renderHRHRGate()}

                    {/* FREE USER GATE (V10 only) */}
                    {renderFreeUserGate()}

                    {/* LIMIT REACHED */}
                    {renderLimitReached()}

                    {/* FORM */}
                    {shouldShowForm() && (
                        <form className="aiassist__form" onSubmit={handleAsk}>
                            <div className="aiassist__input-wrapper">
                                <textarea
                                    className="aiassist__textarea"
                                    disabled={isInputDisabled}
                                    placeholder={getPlaceholderText()}
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    rows={5}
                                />
                            </div>

                            {/* Match Counter */}
                            {renderMatchCounter()}

                            <div className="aiassist__form-footer">
                                <button
                                    type="submit"
                                    className="aiassist__button"
                                    disabled={isSubmitDisabled}
                                    style={{
                                        opacity: isSubmitDisabled ? 0.5 : 1,
                                        cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                                        background: agent === 'basketball' 
                                            ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                                            : agent === 'hrhr'
                                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                                : undefined
                                    }}
                                >
                                    {status === 'loading' 
                                        ? 'Analyzing...' 
                                        : isMatchLimitExceeded 
                                            ? `Too many ${agent === 'basketball' ? 'games' : 'matches'} (${matchCount}/${currentLimits.matches})` 
                                            : agent === 'basketball'
                                                ? '🏀 Analyze Games'
                                                : agent === 'hrhr'
                                                    ? '🎯 Find Value Picks'
                                                    : 'Ask AiPredict'
                                    }
                                </button>
                                {!user && <span className="aiassist__hint">Sign in required.</span>}
                                {user && isMatchLimitExceeded && userTier === 'FREE' && (
                                    <span className="aiassist__hint" style={{ color: '#fbbf24' }}>
                                        🚀 Upgrade to Premium for more {agent === 'basketball' ? 'games' : 'matches'}!
                                    </span>
                                )}
                            </div>
                        </form>
                    )}

                    {errorMessage && <div className="aiassist__error">{errorMessage}</div>}

                    <div ref={answerRef}>
                        {status === 'loading' && renderLoadingStatus()}

                        {(displayedAnswer || fullAnswer) && (
                            <div className="aiassist__answer">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                                    <h2 className="aiassist__answer-title" style={{ margin: 0 }}>
                                        <span className="aiassist__sparkle">
                                            {agent === 'basketball' ? '🏀' : agent === 'hrhr' ? '🎯' : '✨'}
                                        </span> AiPredict Analysis
                                    </h2>
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className="aiassist__button"
                                            onClick={() => setIsShareOpen((v) => !v)}
                                            disabled={status === 'loading'}
                                            style={{ padding: '0.35rem 0.8rem', boxShadow: 'none' }}
                                        >
                                            Share
                                        </button>
                                        {isShareOpen && (
                                            <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', minWidth: '150px', background: 'rgba(2,6,23,0.95)', border: '1px solid rgba(148,163,184,0.18)', borderRadius: '12px', padding: '10px', zIndex: 20 }}>
                                                <button type="button" className="aiassist__upgrade-btn" onClick={() => openShare('twitter')} style={{ width: '100%', marginBottom: '8px' }}>Twitter</button>
                                                <button type="button" className="aiassist__button" onClick={() => openShare('copy')} style={{ width: '100%', padding: '0.35rem', boxShadow: 'none' }}>Copy link</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="aiassist__answer-body">
                                    <ReactMarkdown>{displayedAnswer}</ReactMarkdown>
                                    {status === 'typing' && <span className="typing-cursor">|</span>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT */}
                <div className="aiassist__right">
                    <div className="aiassist__image-wrap">
                        <img src={aiAssistantHero} alt="AiPredict Engine" className="aiassist__image" />
                        <div className="aiassist__image-overlay">
                            <div className="aiassist__badge" style={{
                                background: agent === 'basketball' 
                                    ? 'linear-gradient(135deg, rgba(249, 115, 22, 0.9) 0%, rgba(234, 88, 12, 0.9) 100%)'
                                    : agent === 'hrhr'
                                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)'
                                        : undefined
                            }}>
                                <span className="aiassist__badge-dot" />
                                <span className="aiassist__badge-text">
                                    {getEngineDisplayName()}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="aiassist__stats-card">
                        <div className="aiassist__stats-row">
                            <span className="aiassist__stats-label">Sport</span>
                            <span className="aiassist__stats-value" style={{
                                color: sportType === 'basketball' ? '#f97316' : '#60a5fa'
                            }}>
                                {sportType === 'basketball' ? '🏀 Basketball' : '⚽ Football'}
                            </span>
                        </div>
                        <div className="aiassist__stats-row">
                            <span className="aiassist__stats-label">Engine</span>
                            <span className="aiassist__stats-value" style={{
                                color: agent === 'hrhr' ? '#10b981' : undefined
                            }}>
                                {getEngineModelName()}
                            </span>
                        </div>
                        <div className="aiassist__stats-row">
                            <span className="aiassist__stats-label">Your Tier</span>
                            <span className="aiassist__stats-value" style={{
                                color: userTier === 'PREMIUM' ? '#4ade80' : userTier === 'TRIAL' ? '#fbbf24' : '#60a5fa'
                            }}>
                                {userTier === 'PREMIUM' ? 'Premium' : userTier === 'TRIAL' ? 'Trial' : 'Free'}
                            </span>
                        </div>
                        <div className="aiassist__stats-row">
                            <span className="aiassist__stats-label">Daily Limit</span>
                            <span className="aiassist__stats-value">{currentLimits.searchesPerDay}/day</span>
                        </div>
                        {agent === 'v10' && userTier === 'PREMIUM' && (
                            <div className="aiassist__stats-row">
                                <span className="aiassist__stats-label">Weekly Limit</span>
                                <span className="aiassist__stats-value">{currentLimits.searchesPerWeek}/week</span>
                            </div>
                        )}
                        <div className="aiassist__stats-row">
                            <span className="aiassist__stats-label">Max {agent === 'basketball' ? 'Games' : 'Matches'}</span>
                            <span className="aiassist__stats-value">{currentLimits.matches}</span>
                        </div>
                        {/* 🏀 Basketball Feature Highlight */}
                        {agent === 'basketball' && (
                            <div className="aiassist__stats-row" style={{ 
                                background: 'rgba(249, 115, 22, 0.1)', 
                                padding: '8px', 
                                borderRadius: '8px',
                                marginTop: '8px'
                            }}>
                                <span className="aiassist__stats-label" style={{ color: '#f97316' }}>Features</span>
                                <span className="aiassist__stats-value" style={{ color: '#f97316', fontSize: '0.75rem' }}>
                                    • NBA/Euro
                                </span>
                            </div>
                        )}
                        {/* 🎯 HRHR Feature Highlight */}
                        {agent === 'hrhr' && (
                            <div className="aiassist__stats-row" style={{ 
                                background: 'rgba(16, 185, 129, 0.1)', 
                                padding: '8px', 
                                borderRadius: '8px',
                                marginTop: '8px'
                            }}>
                                <span className="aiassist__stats-label" style={{ color: '#10b981' }}>Features</span>
                                <span className="aiassist__stats-value" style={{ color: '#10b981', fontSize: '0.75rem' }}>
                                    • Value Edge Analysis
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AiAssistant;